import { defineStore, acceptHMRUpdate } from 'pinia'
import type { DeviceAuthToken, UserAuthToken } from '@/api/client'
import { AuthenticationService, DeviceService, DeviceType } from '@/api/client'
import type { Employee } from '@/classes/Employee'
import { useEmployeeStore } from '@/stores/EmployeeStore'
import { SHA256 } from 'crypto-js'
import StoreManager from '@/classes/manager/StoreManager'
import DatabaseManager from '@/classes/manager/DatabaseManager'
import PushNotificationManager from '@/classes/manager/PushNotificationManager'
import { useLocationStore } from '@/stores/LocationStore'
import { getUserStores, getDeviceStores, getUserStoresToSync } from '@/stores'
import { navigateToStartPage } from '@/router/actions/navigateToStartPage'
import handleRequest from '@/helpers/handleRequest'
import { report } from '@/classes/manager/ExceptionReportingManager'
import { useClockReminderStore } from './ClockReminderStore'
import { Clipboard } from '@capacitor/clipboard'
import { DateTime } from 'luxon'
import withLoadingScreen from '@/composables/withLoadingScreen'

const TIMEOUT_AFTER_MS = 30000

type TState = {
  device_token: DeviceAuthToken | null
  user_token: UserAuthToken
  device_type: DeviceType | null
  userSignedUpOnMobile?: boolean
  deviceLoggedInAt: string | null
}

const initialState: TState = {
  device_token: null,
  user_token: null,
  device_type: null,
  userSignedUpOnMobile: undefined,
  deviceLoggedInAt: null,
}

const useAuthStore = defineStore('authStore', {
  state: () => {
    return { ...initialState }
  },
  getters: {
    deviceIsAuthenticated: (state) => {
      return Boolean(state.device_token)
    },
    userIsAuthenticated: (state) => {
      return Boolean(state.user_token)
    },
    isPersonalDevice: (state) => {
      return state.device_type === DeviceType.PERSONAL
    },
    isTeamDevice: (state) => {
      return state.device_type === DeviceType.TEAM
    },
    isTerminalDevice: (state) => {
      return state.device_type === DeviceType.TERMINAL
    },
    isTeamOrTerminal: (state) => {
      return (
        state.device_type === DeviceType.TERMINAL ||
        state.device_type === DeviceType.TEAM
      )
    },
  },
  actions: {
    async login(email: string, password: string) {
      const data = (
        await handleRequest(
          AuthenticationService.loginUser({ email, password }),
          TIMEOUT_AFTER_MS,
        )
      )?.data
      if (!data) return

      await this.loginDevice(data.device_token, data.type)
      try {
        await PushNotificationManager.start()
      } catch (e) {
        report(e as Error)
      }
      await this.loginUser(data.user_token)
    },

    async codeLogin(code: string, setupPushNotifications = true) {
      const data = (
        await handleRequest(
          AuthenticationService.loginViaCode({ code }),
          TIMEOUT_AFTER_MS,
        )
      )?.data
      if (!data) return

      await this.loginDevice(data.device_token, data.type)
      if (setupPushNotifications) {
        try {
          await PushNotificationManager.start()
        } catch (e) {
          report(e as Error)
        }
      }
      await this.loginUser(data.user_token || null)
    },

    async attemptClipboardLogin() {
      try {
        const clipboardText = (await Clipboard.read()).value
        console.log('Clipboard text:', clipboardText)
        if (clipboardText && clipboardText.startsWith('CLOCKIN_LOGIN_TOKEN_')) {
          console.log('Found login token in clipboard')
          const code = clipboardText.replace('CLOCKIN_LOGIN_TOKEN_', '').trim()
          console.log('Extracted code:', code)
          await withLoadingScreen(this.codeLogin(code, false))
          console.log('Navigating to personal app manager new onboarding')
          await Clipboard.write({ string: '' })
          this.userSignedUpOnMobile = true
        } else {
          // handle no clipboard login token found
        }
      } catch (e) {
        console.log('Clipboard login failed', e)
      }
    },

    async loginUserWithPin(employee: Employee, pin: string) {
      const employee_id = employee.id

      const data = (
        await handleRequest(
          AuthenticationService.loginUserWithPin({ employee_id, pin }),
          TIMEOUT_AFTER_MS,
        )
      )?.data

      if (!data) return false

      useEmployeeStore().setActiveEmployee(employee)
      await this.loginUser(data.user_token)
      return true
    },

    async loginDevice(device_token: DeviceAuthToken, device_type: DeviceType) {
      this.device_token = device_token
      this.device_type = device_type

      if (this.device_token) {
        try {
          await StoreManager.sync(getDeviceStores())
          void StoreManager.registerForBackgroundSync([useLocationStore()])

          this.deviceLoggedInAt = DateTime.now().toISO()
        } catch (e) {
          report(e as Error)
        }
      }
    },

    async loginUser(user_token: string | null) {
      this.user_token = user_token
      if (this.user_token) {
        const { employee } = useEmployeeStore()
        if (!employee) return
        await StoreManager.sync(getUserStoresToSync(employee))
      }
    },

    async logout(navToStartPage = true) {
      await this.logoutUser()
      this.logoutDevice(navToStartPage)
    },

    async logoutUser() {
      if (this.user_token) {
        AuthenticationService.logoutUser().catch((e) => console.error(e))
      }
      useEmployeeStore().currentSelectedEmployeeId = undefined
      this.user_token = null
      try {
        await useClockReminderStore().cancelAllNotifications()
      } catch (e) {
        report(e as Error)
      }

      StoreManager.clear(getUserStores())
    },

    logoutDevice(navToStartPage = true) {
      if (this.device_token) {
        AuthenticationService.logoutDevice().catch((e) => console.error(e))
      }
      this.device_type = null
      this.device_token = null
      this.deviceLoggedInAt = null
      this.userSignedUpOnMobile = undefined
      if (navToStartPage) {
        void navigateToStartPage()
      }
      StoreManager.clear(getDeviceStores())
      void DatabaseManager.clearAll()
      void PushNotificationManager.stop()
    },

    checkEmployeePin(employee: Employee, pin: string): boolean {
      if (SHA256(pin).toString() === employee.hashed_pin) {
        return true
      }
      return false
    },

    async changeEmployeePin(employee: Employee, pin: string): Promise<boolean> {
      if (employee.pin_change_required && pin.length === 4) {
        let response
        try {
          response = await DeviceService.changeEmployeePin({
            employee_id: employee.id,
            new_pin: pin,
          })
        } catch (e) {
          console.error(e)
          return false
        }
        if (response.data?.hashed_pin) {
          employee.hashed_pin = response.data?.hashed_pin
          employee.pin_change_required = false
          return true
        }
      }
      return false
    },
    hydrate(state: string) {
      this.$state = JSON.parse(state)
    },
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot))
}

export { useAuthStore }
